/**
 * Dedup Review API
 * Routes through the same choke point as the MCP dedup tools —
 * reconciliation_staging with operation_type = 'dedup_review'.
 */

import { apiFetch } from './config';

export interface DedupTarget {
  pattern_id: string;
  element: string;
  rule: string;
  maturity: string;
  category: string;
  source: string;
  domain: string;
}

export interface DedupCandidate {
  candidate_id?: string;
  invariant: string;
  domain?: string;
  priority?: number;
  rationale?: string;
  consequences?: string;
  exceptions?: string;
  applies_to?: string[];
  source_label?: string;
  extraction_class?: string;
  provenance_class?: string;
  asserting_authority?: string;
  adopting_authority?: string;
}

export interface DedupEvidence {
  cosine_score: number;
  match_class: string;
  match_band: string;
  margin: number | null;
  dedup_method: string;
  unembedded_in_scope?: number;
  intra_batch?: boolean;
  sibling_id?: string;
  sibling_text?: string;
}

export interface DedupReviewItem {
  staging_id: string;
  operation_type: string;
  object_class: string;
  status: string;
  proposed_at: string;
  proposed_by: string;
  age_hours: number;
  target: DedupTarget | null;
  candidate: DedupCandidate;
  evidence: DedupEvidence;
  coordinated_by: string | null;
  coordinated_at: string | null;
  verdict_reason: string | null;
  defer_until: string | null;
  defer_reason: string | null;
}

export interface DedupListResponse {
  items: DedupReviewItem[];
  total: number;
  status: string;
  counts: Record<string, number>;
}

export interface SourceGroup {
  source_project: string;
  item_count: number;
  oldest: string | null;
  newest: string | null;
  items: DedupReviewItem[];
}

export interface DedupGroupedResponse {
  groups: SourceGroup[];
  total_groups: number;
  total_items: number;
  status: string;
  counts: Record<string, number>;
}

export interface SupersedeClaimItem {
  staging_id: string;
  operation_type: string;
  status: string;
  proposed_at: string;
  proposed_by: string;
  age_hours: number;
  target: DedupTarget;
  claimant: DedupTarget & { pattern_id: string | null };
  claim: { reason: string | null; claim_source: string | null };
  evidence: DedupEvidence;
  coordinated_by: string | null;
  coordinated_at: string | null;
  defer_until: string | null;
  defer_reason: string | null;
}

export interface SupersedeListResponse {
  type: 'supersede';
  items: SupersedeClaimItem[];
  total: number;
  status: string;
  counts: Record<string, number>;
}

export interface DedupResolvePayload {
  staging_id: string;
  verdict: 'corroborate' | 'distinct' | 'defer' | 'refine' | 'route_mimir' | 'admit_topic' | 'reject_noise' | 'uphold_supersede' | 'decline_supersede';
  reason?: string;
  defer_reason?: string;
  defer_until?: string;
  topic_id?: string;
  routing_note?: string;
}

export const dedupApi = {
  list: async (status: string = 'pending', limit: number = 200) => {
    const params = new URLSearchParams();
    params.set('status', status);
    params.set('limit', String(limit));
    return apiFetch<DedupListResponse>(`/api/dedup/queue?${params.toString()}`);
  },

  listGrouped: async (status: string = 'pending') => {
    const params = new URLSearchParams();
    params.set('status', status);
    params.set('group_by', 'source');
    return apiFetch<DedupGroupedResponse>(`/api/dedup/queue?${params.toString()}`);
  },

  listSupersede: async (status: string = 'pending', limit: number = 200) => {
    const params = new URLSearchParams();
    params.set('type', 'supersede');
    params.set('status', status);
    params.set('limit', String(limit));
    return apiFetch<SupersedeListResponse>(`/api/dedup/queue?${params.toString()}`);
  },

  resolve: async (payload: DedupResolvePayload) => {
    return apiFetch<{ staging_id: string; verdict: string; result?: unknown }>(
      '/api/dedup/resolve',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );
  },
};
